import { useEffect, useRef, useState } from 'react'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import { useGlobalState } from 'contexts/global-state'
import { ApiService } from 'services/api'

type Props = {
  opened: boolean
  onClose: () => void
  financialMovementId: string | number
}

export default function ReturnedReceiptModal({
  opened,
  onClose,
  financialMovementId,
}: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [receiptBlob, setReceiptBlob] = useState<Blob | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { openSuccessToast, openErrorToast } = useGlobalState()

  useEffect(() => {
    if (!opened || !financialMovementId) return
    setIsLoading(true)
    setReceiptBlob(null)
    ApiService.FinancialMovements.getReturnedReceipt(
      String(financialMovementId),
    )
      .then((blob) => {
        setReceiptBlob(blob)
      })
      .catch(() => {
        setReceiptBlob(null)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [opened, financialMovementId])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setIsUploading(true)
    try {
      await ApiService.FinancialMovements.uploadReturnedReceipt(
        String(financialMovementId),
        file,
      )
      openSuccessToast({ message: 'Comprovante enviado com sucesso.' })
      const blob = await ApiService.FinancialMovements.getReturnedReceipt(
        String(financialMovementId),
      )
      setReceiptBlob(blob)
    } catch {
      openErrorToast({ message: 'Erro ao enviar comprovante.' })
    } finally {
      setIsUploading(false)
    }
  }

  const handleDownload = () => {
    if (!receiptBlob) return
    const url = URL.createObjectURL(receiptBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `comprovante-devolucao-${financialMovementId}.pdf`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await ApiService.FinancialMovements.deleteReturnedReceipt(
        String(financialMovementId),
      )
      openSuccessToast({ message: 'Comprovante removido com sucesso.' })
      setReceiptBlob(null)
    } catch {
      openErrorToast({ message: 'Erro ao remover comprovante.' })
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Modal open={opened} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: '8px',
          p: 4,
        }}
      >
        <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
          Comprovante de Devolução
        </Typography>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading
                ? 'Enviando...'
                : receiptBlob
                ? 'Substituir comprovante'
                : 'Upload comprovante (PDF)'}
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleDownload}
              disabled={!receiptBlob}
            >
              Baixar comprovante
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={handleDelete}
              disabled={!receiptBlob || isDeleting}
            >
              {isDeleting ? 'Removendo...' : 'Remover comprovante'}
            </Button>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button onClick={onClose} disabled={isUploading || isDeleting}>
            Fechar
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
